// What each pushed event makes stale: the only hand-written part of the UI's reactivity. Both
// sides are generated — `AppEvent` from the bot's enum, `ContractPath` from the OpenAPI document —
// so an event the table forgets, a route it misspells, or a route the bot drops fails `tsc`.
// [[spec-reactive-arb-bot-ui-over-server-sent-events-generated-from-one-rust-event-enum]]

import type { QueryClient } from "@tanstack/react-query";

import type { AppEvent } from "@/generated/api/model";
import { type ContractPath, toPattern } from "@/lib/api/contract";

const EVERYTHING = "everything";

export const INVALIDATES = {
    arbitrages_changed: ["/api/arbitrages"],
    opportunities_changed: [
        "/api/opportunities",
        "/api/opportunities/stats",
        "/api/opportunities/{id}",
        "/api/opportunities/thresholds",
        "/api/opportunities/notifications",
    ],
    arbitrage_history_changed: ["/api/arbitrages/history"],
    pools_changed: ["/api/dexes", "/api/pools/{dex}", "/api/pairs"],
    chain_advanced: [
        "/api/regulus/point",
        "/api/regulus/utxo",
        "/api/regulus/address/{address}",
        "/api/utxos",
        "/api/addresses/{address}",
        "/api/wallet/point",
        "/api/wallet/utxo",
    ],
    resync: EVERYTHING,
} as const satisfies Record<AppEvent, readonly ContractPath[] | typeof EVERYTHING>;

// Orval keys every query by its concrete path first (`["/api/pools/minswap", params]`), so a
// route template matches all of its path and query parameters at once, and only its own route:
// `/api/arbitrages` leaves `/api/arbitrages/history` alone.
const PATTERNS = new Map(
    Object.entries(INVALIDATES).flatMap(([event, paths]) =>
        paths === EVERYTHING ? [] : [[event, paths.map(toPattern)] as const],
    ),
);

export function invalidate(queryClient: QueryClient, events: Iterable<AppEvent>): void {
    const patterns: RegExp[] = [];
    for (const event of events) {
        if (INVALIDATES[event] === EVERYTHING) {
            void queryClient.invalidateQueries();
            return;
        }
        patterns.push(...(PATTERNS.get(event) ?? []));
    }
    if (patterns.length === 0) return;
    void queryClient.invalidateQueries({
        predicate: ({ queryKey: [path] }) => typeof path === "string" && patterns.some((p) => p.test(path)),
    });
}

/** How long a burst is gathered: one block lands as a handful of events within milliseconds. */
const COALESCE_MS = 100;

/**
 * Gathers a burst of events and invalidates their union once, so a block that changes ten pairs
 * refetches the arbitrage list once. A refetch already in flight is cancelled and restarted on
 * purpose: it started before the change, so joining it would show data the event just outdated.
 */
export function createInvalidator(queryClient: QueryClient, windowMs = COALESCE_MS) {
    const pending = new Set<AppEvent>();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
        timer = undefined;
        const events = [...pending];
        pending.clear();
        invalidate(queryClient, events);
    };

    return {
        push(event: AppEvent) {
            pending.add(event);
            timer ??= setTimeout(flush, windowMs);
        },
        cancel() {
            clearTimeout(timer);
            pending.clear();
        },
    };
}
